import React, { FC, Fragment, useState, useEffect } from 'react'
import { MegaMenu, Container } from '../components'
import { errorTypes, PROVIDER_NONE } from '../utils/constants'
import BadRequestError from './errors/BadRequestError'
import ServerError from './errors/ServerError'
import {
  getAllowedShowingProvidersRequest,
  saveDefaultShowingProviderRequest,
  Provider,
  ProviderLevel,
} from 'utils/api/showingChoiceServicesRequests'
import { Config, useConfig } from 'config'
import ShowingProviderSuccess from './notifications/ShowingProviderSuccess'
import ShowingChoiceSuccess from './notifications/ShowingChoiceSuccess'
import ShowingTransferWarning from './notifications/ShowingTransferWarning'
import ShowingBurnoutWarning from './notifications/ShowingBurnoutWarning'
import SentriKeyWarning from './notifications/SentriKeyWarning'
import { initializePendo, switchRegionCognitoConfig } from 'utils/helper/helper'

interface IShowingChoiceProps {
  memberKey: string
}

const ShowingChoice: FC<IShowingChoiceProps> = ({
  memberKey = '',
}: IShowingChoiceProps) => {
  const [loading, setLoading] = useState<boolean>(true)
  const [isChanged, setIsChanged] = useState(false)
  const [isSaveable, setIsSaveable] = useState(false)
  const [isAllowingManagment, setIsAllowingManagment] = useState(false)
  const [errorType, setErrorType] = useState<string | null>(null)
  const [allowShowings, setAllowShowings] = useState('Yes')
  const [providers, setProviders] = useState<Provider[]>()
  const [previousShowingProvider, setPreviousShowingProvider] = useState('')
  const [selectedShowingProvider, setSelectedShowingProvider] = useState('')
  const [bulkTransfer, setBulkTransfer] = useState(false)
  const [burnout, setBurnout] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const { config, setConfig } = useConfig()
  const allowShowingsChoices = ['Yes', 'No']
  const [showProviderSuccess, setShowProviderSuccess] = useState(false)
  const [showChoiceSuccess, setShowChoiceSuccess] = useState(false)
  const [showSentriKeyWarning, setShowSentriKeyWarning] = useState(false)

  const closeNotifications = () => {
    setShowChoiceSuccess(false)
    setShowProviderSuccess(false)
    setBulkTransfer(false)
    setBurnout(false)
  }

  // get user data
  const getAllowedShowingProviders = async (config: Config) => {
    if (config) {
      try {
        const response = await getAllowedShowingProvidersRequest(
          config,
          memberKey,
        )
        if (response != null) {
          const providers: Provider[] = response.providers
          setProviders(providers)
          if (response.currentProvider?.name) {
            setPreviousShowingProvider(response.currentProvider.name)
            setSelectedShowingProvider(response.currentProvider.name)
          }
          if (
            providers.length === 0 ||
            response.currentProvider?.guid === PROVIDER_NONE
          ) {
            setAllowShowings('No')
            setIsAllowingManagment(false)
          } else {
            setAllowShowings('Yes')
            setIsAllowingManagment(true)
          }
        }
      } catch (error: any) {
        console.error('Error calling getAllowedShowingProviders:', error)
        try {
          if (JSON.parse(error.message).status === 401) {
            await switchRegionCognitoConfig(config, setConfig)
          }
        } catch (error) {}
      } finally {
        setLoading(false)
      }
    }
  }
  useEffect(() => {
    initializePendo()
    if (config) {
      getAllowedShowingProviders(config)
    }
  }, [config])

  const handleAllowShowingsChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setAllowShowings(event.target.value)
    setIsAllowingManagment(event.target.value === 'Yes')
    setIsChanged(true)
    setIsSaveable((prevState) => prevState || event.target.value === 'No')
    closeNotifications()
  }

  const handleShowingProviderChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newSelectedProvider = event.target.value
    setSelectedShowingProvider(newSelectedProvider)
    setIsChanged(true)
    setIsSaveable(true)
    closeNotifications()

    if (
      newSelectedProvider.toLowerCase().includes('sentrikey') &&
      !previousShowingProvider.toLowerCase().includes('sentrikey')
    ) {
      setShowSentriKeyWarning(true)
    } else {
      setShowSentriKeyWarning(false)
    }

    if (providers) {
      const currentProvider = providers.find(
        (provider) => provider.name === previousShowingProvider,
      )
      const selectedProvider = providers.find(
        (provider) => provider.name === newSelectedProvider,
      )

      if (
        currentProvider?.level === ProviderLevel.Listing &&
        selectedProvider?.level === ProviderLevel.Listing
      ) {
        setBulkTransfer(false)
        setBurnout(true)
      } else if (
        currentProvider?.level === ProviderLevel.Member ||
        selectedProvider?.level === ProviderLevel.Member
      ) {
        setBulkTransfer(true)
        setBurnout(false)
      } else {
        setBulkTransfer(false)
        setBurnout(false)
      }
    }
  }

  const handleSentriKeyConfirm = () => {
    setShowSentriKeyWarning(false)
  }

  const handleCloseError = () => {
    setErrorType(null)
  }

  const renderErrorComponent = () => {
    switch (errorType) {
      case errorTypes.BAD_REQUEST:
        return <BadRequestError onClose={handleCloseError} />
      case errorTypes.SERVER:
        return <ServerError onClose={handleCloseError} />
      default:
        return null
    }
  }

  const handleSave = async () => {
    if (!config) {
      return
    }

    // Close any existing notifications before saving
    closeNotifications()
    setIsSaving(true)

    try {
      if (providers) {
        const searchName =
          allowShowings === 'No' ? 'None' : selectedShowingProvider
        const matchedProvider = providers.find(
          (provider) => provider.name === searchName,
        )

        if (matchedProvider) {
          await saveDefaultShowingProviderRequest(
            config,
            memberKey,
            matchedProvider.guid,
          )
          setIsChanged(false)
          setIsSaveable(false)
          setErrorType(null)

          setPreviousShowingProvider(selectedShowingProvider)

          // Show appropriate success message
          if (allowShowings === 'No') {
            setShowChoiceSuccess(true)
            setSelectedShowingProvider('')
            setPreviousShowingProvider('')
          } else {
            setShowProviderSuccess(true)
          }
        }
      }
    } catch (error: any) {
      try {
        console.error('Error Saving default provider', error)
        if (JSON.parse(error.message).status === 401) {
          await switchRegionCognitoConfig(config, setConfig)
          return
        }
      } catch (error) {}

      if (error.statusCode === 500) {
        setErrorType(errorTypes.SERVER)
      } else {
        setErrorType(errorTypes.BAD_REQUEST)
      }
    } finally {
      setIsSaving(false)
    }
  }

  const content = (
    <div className="indent">
      {renderErrorComponent()}
      <div>
        <h2>Showing Management</h2>
        <p>Manage and set your listing(s) default showing preferences.</p>
      </div>

      <div className="indent">
        <h3>Showing Preferences</h3>

        <div>
          <p>
            Set your preferences from the options below. Be sure to save your
            changes before leaving this page to ensure they are applied.
          </p>
        </div>
        <div>
          <h4>
            Do you wish to allow showings to be managed for listings you enter
            in Bright?
          </h4>
          {allowShowingsChoices
            .filter((option) => {
              const isYes = option === 'Yes'
              const hasNoProviders = !providers || providers.length < 1
              const onlyNoneProvider =
                providers?.length === 1 && providers[0]?.guid === PROVIDER_NONE
              return !(isYes && (hasNoProviders || onlyNoneProvider))
            })
            .map((option, index) => (
              <div key={index} className="label">
                <label key={option}>
                  <input
                    type="radio"
                    name="AllowShowings"
                    value={option}
                    checked={allowShowings === option}
                    onChange={handleAllowShowingsChange}
                  />
                  {option}
                </label>
              </div>
            ))}
          <div style={{ maxWidth: '600px' }}>
            {showChoiceSuccess && (
              <ShowingChoiceSuccess onClose={closeNotifications} />
            )}
          </div>
        </div>

        <div>
          <p className={`subcontent ${allowShowings !== 'No' ? 'hidden' : ''}`}>
            By Selecting &quot;No&quot;, your new listings will no longer
            include a showing management service. Note: All previously published
            active listings that used SentriKey Showing Service will no longer
            retain them as their existing showing management service. However,
            any previously published active listings that used ShowingTime will
            continue to retain them as their existing showing management
            service.
          </p>
        </div>

        <div className={`${allowShowings === 'No' ? 'hidden' : ''}`}>
          <br />
          <div>
            <h4>Showing Management Service:</h4>
            <div className="label">
              <label>Select your preferred showing management service.</label>
              <br />
              <br />
              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {(providers ?? [])
                  .filter((provider) => provider.guid !== PROVIDER_NONE)
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((provider) => (
                    <label key={provider.guid} className="label">
                      <input
                        type="radio"
                        name="provider"
                        value={provider.name}
                        checked={selectedShowingProvider === provider.name}
                        onChange={handleShowingProviderChange}
                      />
                      {provider.name}
                    </label>
                  ))}
              </div>

              <div
                className={`${previousShowingProvider === selectedShowingProvider ? 'hidden' : ''}`}
              >
                <div style={{ maxWidth: '600px' }}>
                  {bulkTransfer && (
                    <ShowingTransferWarning onClose={closeNotifications} />
                  )}
                  {burnout && (
                    <ShowingBurnoutWarning onClose={closeNotifications} />
                  )}
                  {showSentriKeyWarning && (
                    <SentriKeyWarning
                      onClose={closeNotifications}
                      onConfirm={handleSentriKeyConfirm}
                      sentrikeyAccountSetupUrl={
                        config?.sentrikeyAccountSetupUrl
                      }
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ maxWidth: '600px' }}>
          {showProviderSuccess && (
            <ShowingProviderSuccess onClose={closeNotifications} />
          )}
        </div>

        <br />

        <button
          onClick={handleSave}
          disabled={
            !isChanged ||
            !isSaveable ||
            isSaving ||
            (previousShowingProvider === selectedShowingProvider &&
              isAllowingManagment) ||
            (showSentriKeyWarning && allowShowings !== 'No')
          }
        >
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  )

  let body
  if (!loading) {
    body = content
  }

  return (
    <Fragment>
      <MegaMenu />
      <Container>{body}</Container>
    </Fragment>
  )
}
export default ShowingChoice
